import { useState } from "react";
import { installed, isIPhone, keptWhen, useInstallPrompt, useKept, useOnline } from "./offline.ts";
import { Button, Icon } from "./souvenir.ts";

// Offline, on a weak signal, or Tripmo not answering: what's on screen is what this device kept, and since
// when ("No signal. The trip as this phone kept it at 14:02."). Why, as the service worker saw it
// (web/sw/sw.ts): "No signal." where the device has none, "Tripmo isn't answering." only where Tripmo
// answered with a server error, and "The signal may be weak." where no answer came at all, or none in time
// (the lead's call, 2026-10-24: a weak signal was said as Tripmo not answering). A change tried meanwhile
// says it needs signal. Nothing while everything came from Tripmo.
export function NoSignal({ what }: { what: "the trip" | "your trips" }) {
  const online = useOnline();
  const kept = useKept();
  if (online && !kept) return null;
  const device = matchMedia("(pointer: coarse)").matches ? "phone" : "computer";
  const shown = what === "the trip" ? `The trip as this ${device} kept it` : `Your trips as this ${device} kept them`;
  const why = !online ? "No signal." : kept?.tripmoErred ? "Tripmo isn't answering." : "The signal may be weak.";
  return (
    <div className="tm-no-signal" role="status">
      <span className="callout">
        {why}
        {kept && ` ${shown} at ${keptWhen(kept.since)}.`}
      </span>
    </div>
  );
}

const DISMISSED = "tripmo:install-hint";

// Whether to suggest installing: on a phone that hasn't installed Tripmo, until dismissed there.
export function useInstallHint(): { show: boolean; dismiss: () => void } {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED) === "dismissed";
    } catch {
      return false;
    }
  });
  const phone = matchMedia("(pointer: coarse) and (max-width: 899px)").matches;
  return {
    show: phone && !dismissed && !installed(),
    dismiss: () => {
      setDismissed(true);
      try {
        localStorage.setItem(DISMISSED, "dismissed");
      } catch {}
    },
  };
}

// How to install, once (iPhones never offer it). On an iPhone, installing is also what lets
// notifications in.
export function InstallHint({ onDismiss: dismiss }: { onDismiss: () => void }) {
  const prompt = useInstallPrompt();
  return (
    <aside className="tm-install" aria-label="Keep Tripmo on your home screen">
      <div className="tm-install-text">
        <h2 className="title">Keep Tripmo on your home screen</h2>
        <p className="callout tm-muted">
          For when there's no signal: each trip you open here stays on this phone, with its tickets and files.
          {isIPhone() && " And reminders arrive on it, as notifications."}
        </p>
        {isIPhone() ? (
          <p className="callout tm-install-how">
            Tap Share <Icon name="share" size={18} label="Share" className="tm-install-icon" />, then{" "}
            <span className="tm-nowrap">Add to Home Screen</span>.
          </p>
        ) : prompt ? (
          <Button size="sm" icon="plus" className="tm-start" onClick={() => prompt.prompt().catch(() => {})}>
            Install Tripmo
          </Button>
        ) : (
          <p className="callout tm-install-how">From your browser's menu, choose Install app or Add to Home screen.</p>
        )}
      </div>
      <button type="button" className="tm-install-close" aria-label="Not now" onClick={dismiss}>
        <Icon name="close" size={18} />
      </button>
    </aside>
  );
}
